import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { TraitAssessmentResult } from "@dimensional-engineering/dimensional-models";
import { Roboto_Mono } from "next/font/google";

import styles from "./TraitAssessmentOutro.module.css";

import elementJsonData from "../../../assets/traitInfo/elements.json";

import { useStaggeredOpacity } from "@/helpers/useStaggeredOpacity";
import { ExtendedPattern, ExtendedScale } from "@/models/sharedModels";
import Element from "@/components/traits/element/Element/Element";
import Pattern from "@/components/traits/pattern/Pattern/Pattern";
import {
  getUserPatternScore,
  getUserScaleScore,
} from "@/components/traits/utils";
import { PremiumExclusiveDeniedBAr } from "@/components/premium/PremiumTraitAccessDeniedBar/PremiumTraitAccessDeniedBar";
import ThemedButton, {
  DimensionalPlusThemedButton,
} from "@/components/shared/ThemedButton/ThemedButton";
import { useModalContext } from "@/context/ModalContext";
import {
  PeriodicQuizInstagramIcon,
  PeriodicQuizSnapchatIcon,
} from "@/components/appHome/PeriodicQuizzes/PeriodicQuizShareIcons";
import ElementAndPatternSharedModal from "@/components/traits/TraitsModals/ElementAndPatternSharedModal/ElementAndPatternSharedModal";
import {
  getPreviousDimensionSlugSource,
  getPreviousIsLongFormGenerated,
  getPreviousPercentScoreFromScoreHistory,
  getPreviousPercentScoreFromScoreHistoryPatterns,
} from "@/components/assessments-new/assessmentUtils";
import { ForwardArrowDeltaScreen } from "@/components/assessments-new/views/AsessmentOutroScreen/ArchetypeDiscovery/ArchetypeDiscoveryScreen";
import Archetype from "@/components/traits/arcehtype/Archetypes/Archetype";
import SmallProfileImage from "@/components/shared/SmallProfileImage/SmallProfileImage";
import ConversionModal from "@/components/premium/ConversionModal/ConversionModal";
import { useAuth } from "@/context/AuthContext";
import { Mixpanel } from "@/helpers/mixpanel";

type Props = {
  traitAssessmentResult: TraitAssessmentResult;
  type: "element" | "pattern";
  slug: string;
  isPremium?: boolean;
  element: ExtendedScale | undefined;
  pattern: ExtendedPattern | undefined;
};

const font = Roboto_Mono({ subsets: ["latin"] });

export function TraitAssessmentOutroScaleReveal(props: Props) {
  const opacity = useStaggeredOpacity(3);

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        TRAIT
      </p>

      <div
        style={{ opacity: opacity.step2, flex: 2 }}
        className={styles.scaleRevealTextDiv}
      >
        <p className={styles.scaleRevealText}>You discovered a new score for</p>
        <p className={styles.scaleRevealName}>
          {props.type === "element"
            ? props.element?.alias ?? props.element?.name
            : props.pattern?.name}
        </p>
      </div>

      <div
        className={styles.scaleRevealIcon}
        style={{ opacity: opacity.step3, flex: 5 }}
      >
        {props.type === "element" ? (
          <Element slug={props.slug} maxWidth />
        ) : (
          <Pattern patternSlug={props.slug} maxWidth />
        )}
      </div>
    </main>
  );
}

export function TraitAssessmentOutroScaleDefinition(props: Props) {
  const isUserPremium = useSelector((state: RootState) => state.user.isPremium);

  const premiumGated = props.isPremium && !isUserPremium;

  const opacity = useStaggeredOpacity(3);

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        DEFINITION
      </p>

      <div
        className={styles.scaleRevealIcon}
        style={{ opacity: opacity.step2, flex: 2 }}
      >
        {props.type === "element" ? (
          <Element slug={props.slug} maxWidth />
        ) : (
          <Pattern patternSlug={props.slug} maxWidth />
        )}
      </div>

      <p className={styles.llmText} style={{ opacity: opacity.step3, flex: 5 }}>
        {premiumGated
          ? props.type === "element"
            ? props.element?.shortDescription
            : props.pattern?.shortDescription
          : props.traitAssessmentResult.traitAssessmentLlmData?.definitionBody}
      </p>
    </main>
  );
}

export function TraitAssessmentOutroScoreReveal(props: Props) {
  const { setModalComponent } = useModalContext();

  const { user } = useAuth();

  const isUserPremium = useSelector((state: RootState) => state.user.isPremium);

  const premiumGated = props.isPremium && !isUserPremium;

  const opacity = useStaggeredOpacity(4);

  const score =
    props.type == "element"
      ? getUserScaleScore(
          props.traitAssessmentResult.scaleScore?.percentScore,
          props.slug,
          props.traitAssessmentResult.scaleScore?.dimensionSlugSource
        )
      : getUserPatternScore(
          props.traitAssessmentResult.patternDiscovery?.percentScore,
          props.slug,
          props.traitAssessmentResult.patternDiscovery?.isLongFormGenerated
        );

  const scoreText = score ? (score * 100).toFixed(0) : 0;

  if (premiumGated) {
    return (
      <main className={styles.main}>
        <p
          style={{ opacity: opacity.step1, flex: 1 }}
          className={styles.subtitle}
        >
          SCORE
        </p>
        <div
          className={styles.scoreRevealDiv}
          style={{ opacity: opacity.step2, flex: 2 }}
        >
          <h1 className={styles.scoreRevealBigText}>??</h1>
          <div className={styles.scoreRevealDivInner}>
            <p className={`${styles.yourScoreText} ${font.className}`}>
              Your score
            </p>
            <p className={`${styles.outOfText} ${font.className}`}>
              (out of 100)
            </p>
          </div>
        </div>
        <div
          className={styles.linearDiv}
          style={{ opacity: opacity.step3, flex: 3 }}
        >
          <PremiumExclusiveDeniedBAr />
        </div>

        <div
          className={styles.bottomPlusDiv}
          style={{ opacity: opacity.step4, flex: 2 }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DimensionalPlusThemedButton
            onClick={() => {
              Mixpanel?.track(
                "Premium Conversion Modal Summoned",
                {
                  source: "tracked trait assessment outro",
                },
                user?.uid
              );
              setModalComponent(<ConversionModal />);
            }}
          />
          <p className={styles.learnMoreText}>Learn more about Plus</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        SCORE
      </p>
      <div
        className={styles.scoreRevealDiv}
        style={{ opacity: opacity.step2, flex: 3 }}
      >
        <h1 className={styles.scoreRevealBigText}>{scoreText}</h1>
        <div className={styles.scoreRevealDivInner}>
          <p className={`${styles.yourScoreText} ${font.className}`}>
            Your score
          </p>
          <p className={`${styles.outOfText} ${font.className}`}>
            (out of 100)
          </p>
        </div>
      </div>
      <p className={styles.llmText} style={{ opacity: opacity.step3, flex: 5 }}>
        {props.traitAssessmentResult.traitAssessmentLlmData?.scoreRevealBody}
      </p>

      <div
        className={styles.shareOuterDiv}
        style={{ opacity: opacity.step4, flex: 2 }}
      >
        <p className={styles.shareToText}>Share results to:</p>
        <div className={styles.shareInnerDiv}>
          <PeriodicQuizInstagramIcon
            onClick={() => {
              setModalComponent(
                <ElementAndPatternSharedModal
                  traitSlug={props.slug}
                  traitType={props.type}
                  type="instagram"
                />
              );
            }}
          />
          <PeriodicQuizSnapchatIcon
            onClick={() => {
              setModalComponent(
                <ElementAndPatternSharedModal
                  traitSlug={props.slug}
                  traitType={props.type}
                  type="snapchat"
                />
              );
            }}
          />
        </div>
      </div>
    </main>
  );
}

export function TraitAssessmentOutroScoreChange(props: Props) {
  const isUserPremium = useSelector((state: RootState) => state.user.isPremium);

  const premiumGated = props.isPremium && !isUserPremium;

  const opacity = useStaggeredOpacity(4);

  const score =
    props.type == "element"
      ? getUserScaleScore(
          props.traitAssessmentResult.scaleScore?.percentScore,
          props.slug,
          props.traitAssessmentResult.scaleScore?.dimensionSlugSource
        )
      : getUserPatternScore(
          props.traitAssessmentResult.patternDiscovery?.percentScore,
          props.slug,
          props.traitAssessmentResult.patternDiscovery?.isLongFormGenerated
        );

  const previousPercentScore =
    props.type == "element"
      ? getPreviousPercentScoreFromScoreHistory(
          props.traitAssessmentResult.scaleScore?.scoreHistory
        )
      : getPreviousPercentScoreFromScoreHistoryPatterns(
          props.traitAssessmentResult.patternDiscovery?.scoreHistory
        );

  const previousDimensionSlug = getPreviousDimensionSlugSource(
    props.traitAssessmentResult.scaleScore?.scoreHistory
  );
  const previousIsLongFormGenerated = getPreviousIsLongFormGenerated(
    props.traitAssessmentResult.patternDiscovery?.scoreHistory
  );

  const previousScore =
    props.type == "element"
      ? getUserScaleScore(
          previousPercentScore,
          props.slug,
          previousDimensionSlug
        )
      : getUserPatternScore(
          previousPercentScore,
          props.slug,
          previousIsLongFormGenerated
        );

  const displayedScore = score ? (score * 100).toFixed(0) : 0;

  const displayedPreviousScore = previousScore
    ? (previousScore * 100).toFixed(0)
    : 0;

  if (premiumGated) {
    return (
      <main className={styles.main}>
        <p
          style={{ opacity: opacity.step1, flex: 1 }}
          className={styles.subtitle}
        >
          SCORE HISTORY
        </p>

        <div
          style={{ opacity: opacity.step2, flex: 3 }}
          className={styles.scoreHistoryParent}
        >
          <div className={styles.scoreHistoryParentInner}>
            <p className={styles.scoreHistoryParentScore}>
              {"??"}
              <span className={styles.scoreHistorySpan}>/100</span>
            </p>
            <p className={`${font.className} ${styles.scoreHistoryBottomText}`}>
              Previous score
            </p>
          </div>

          <ForwardArrowDeltaScreen />

          <div className={styles.scoreHistoryParentInner}>
            <p className={styles.scoreHistoryParentScore}>
              {"??"}
              <span className={styles.scoreHistorySpan}>/100</span>
            </p>
            <p className={`${font.className} ${styles.scoreHistoryBottomText}`}>
              {"Today's score"}
            </p>
          </div>
        </div>

        <div
          className={styles.linearDiv}
          style={{ opacity: opacity.step3, flex: 3 }}
        >
          <PremiumExclusiveDeniedBAr />
        </div>

        {props.type === "element" && (
          <div className={styles.buttonDiv} style={{ opacity: opacity.step4 }}>
            <ThemedButton
              style={{ width: "100%", fontWeight: "450", padding: "14px" }}
              title="Continue to breakdown"
            />
          </div>
        )}
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        SCORE HISTORY
      </p>

      <div
        style={{ opacity: opacity.step2, flex: 3 }}
        className={styles.scoreHistoryParent}
      >
        <div className={styles.scoreHistoryParentInner}>
          <p className={styles.scoreHistoryParentScore}>
            {displayedPreviousScore}
            <span className={styles.scoreHistorySpan}>/100</span>
          </p>
          <p className={`${font.className} ${styles.scoreHistoryBottomText}`}>
            Previous score
          </p>
        </div>

        <ForwardArrowDeltaScreen />

        <div className={styles.scoreHistoryParentInner}>
          <p className={styles.scoreHistoryParentScore}>
            {displayedScore}
            <span className={styles.scoreHistorySpan}>/100</span>
          </p>
          <p className={`${font.className} ${styles.scoreHistoryBottomText}`}>
            {"Today's score"}
          </p>
        </div>
      </div>

      <p className={styles.llmText} style={{ opacity: opacity.step3, flex: 4 }}>
        {props.traitAssessmentResult.traitAssessmentLlmData?.scoreChangeBody}
      </p>

      <p
        className={styles.bottomWarning}
        style={{ opacity: opacity.step4, flex: 1 }}
      >
        {"*It's normal for some trait scores to change."} <br />
        {"Change isn't “good” or “bad”. It just is."}
      </p>
    </main>
  );
}

export function TraitAssessmentOutroComponents(props: Props) {
  const isUserPremium = useSelector((state: RootState) => state.user.isPremium);
  const elementDiscovery = useSelector(
    (state: RootState) => state.user.elementsDiscovered
  );

  const premiumGated = props.isPremium && !isUserPremium;

  const opacity = useStaggeredOpacity(4);

  const mapped = props?.pattern?.blendedRules
    .sort((a, b) => {
      const foundA = elementDiscovery?.find((e) => e.scaleSlug === a.slug);
      const foundB = elementDiscovery?.find((e) => e.scaleSlug === b.slug);

      const currentScoreA =
        getUserScaleScore(
          foundA?.scaleScore?.percentScore,
          a.slug,
          foundA?.scaleScore?.dimensionSlugSource
        ) ?? 0;
      const currentScoreB =
        getUserScaleScore(
          foundB?.scaleScore?.percentScore,
          b.slug,
          foundB?.scaleScore?.dimensionSlugSource
        ) ?? 0;

      const previousPercentScoreA = getPreviousPercentScoreFromScoreHistory(
        foundA?.scaleScore?.scoreHistory
      );
      const previousPercentScoreB = getPreviousPercentScoreFromScoreHistory(
        foundB?.scaleScore?.scoreHistory
      );

      const previousDimensionSlugSourceA = getPreviousDimensionSlugSource(
        foundA?.scaleScore?.scoreHistory
      );
      const previousDimensionSlugSourceB = getPreviousDimensionSlugSource(
        foundB?.scaleScore?.scoreHistory
      );

      const previouseScoreA =
        getUserScaleScore(
          previousPercentScoreA,
          a.slug,
          previousDimensionSlugSourceA
        ) ?? 0;
      const previouseScoreB =
        getUserScaleScore(
          previousPercentScoreB,
          b.slug,
          previousDimensionSlugSourceB
        ) ?? 0;

      const deltaA = Math.abs(currentScoreA - previouseScoreA);
      const deltaB = Math.abs(currentScoreB - previouseScoreB);

      if (deltaA > deltaB) {
        return -1;
      } else if (deltaA < deltaB) {
        return 1;
      } else {
        return 0;
      }
    })
    .map((el) => {
      return (
        <div key={el.slug} className={styles.scaleRevealIcon}>
          <Element slug={el.slug} />
        </div>
      );
    });

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        COMPONENTS
      </p>

      <div style={{ flex: 3 }} className={styles.componentParentDiv}>
        <div
          className={styles.scaleRevealIcon}
          style={{ opacity: opacity.step2 }}
        >
          <Pattern patternSlug={props.slug} maxWidth />
        </div>
        <div
          style={{ opacity: opacity.step3 }}
          className={styles.componentLine}
        ></div>
        <div
          style={{ opacity: opacity.step3 }}
          className={styles.componentWrapper}
        >
          {mapped}
        </div>
      </div>

      {premiumGated ? (
        <div
          className={styles.linearDiv}
          style={{ opacity: opacity.step4, flex: 3 }}
        >
          <PremiumExclusiveDeniedBAr />
        </div>
      ) : (
        <p
          className={styles.llmText}
          style={{ opacity: opacity.step4, flex: 4 }}
        >
          {
            props.traitAssessmentResult.traitAssessmentLlmData
              ?.subcomponentRevealBody
          }
        </p>
      )}
    </main>
  );
}

export function TraitAssessmentOutroComponentsDelta(props: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isUserPremium = useSelector((state: RootState) => state.user.isPremium);
  const elementDiscovery = useSelector(
    (state: RootState) => state.user.elementsDiscovered
  );

  const premiumGated = props.isPremium && !isUserPremium;

  const opacity = useStaggeredOpacity(5);

  const sorted = props?.pattern?.blendedRules.sort((a, b) => {
    const foundA = elementDiscovery?.find((e) => e.scaleSlug === a.slug);
    const foundB = elementDiscovery?.find((e) => e.scaleSlug === b.slug);

    const currentScoreA =
      getUserScaleScore(
        foundA?.scaleScore?.percentScore,
        a.slug,
        foundA?.scaleScore?.dimensionSlugSource
      ) ?? 0;
    const currentScoreB =
      getUserScaleScore(
        foundB?.scaleScore?.percentScore,
        b.slug,
        foundB?.scaleScore?.dimensionSlugSource
      ) ?? 0;

    const previousPercentScoreA = getPreviousPercentScoreFromScoreHistory(
      foundA?.scaleScore?.scoreHistory
    );
    const previousPercentScoreB = getPreviousPercentScoreFromScoreHistory(
      foundB?.scaleScore?.scoreHistory
    );

    const previousDimensionSlugSourceA = getPreviousDimensionSlugSource(
      foundA?.scaleScore?.scoreHistory
    );
    const previousDimensionSlugSourceB = getPreviousDimensionSlugSource(
      foundB?.scaleScore?.scoreHistory
    );

    const previouseScoreA =
      getUserScaleScore(
        previousPercentScoreA,
        a.slug,
        previousDimensionSlugSourceA
      ) ?? 0;
    const previouseScoreB =
      getUserScaleScore(
        previousPercentScoreB,
        b.slug,
        previousDimensionSlugSourceB
      ) ?? 0;

    const deltaA = Math.abs(currentScoreA - previouseScoreA);
    const deltaB = Math.abs(currentScoreB - previouseScoreB);

    if (deltaA > deltaB) {
      return -1;
    } else if (deltaA < deltaB) {
      return 1;
    } else {
      return 0;
    }
  });

  const mapped = sorted?.map((el, i) => {
    return (
      <div
        style={i === 0 ? { opacity: 1 } : { opacity: !premiumGated ? 0.2 : 1 }}
        className={styles.scaleRevealIcon}
        key={el.slug}
      >
        <Element slug={el.slug} />
      </div>
    );
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      console.log("no ref");
      return;
    }

    const isOverflowing = container.scrollWidth > container.clientWidth;
    container.style.justifyContent = isOverflowing ? "flex-start" : "center";
  }, [containerRef, sorted]);

  const biggestDelta = sorted ? sorted[0] : undefined;
  const biggestDeltaFound = elementDiscovery?.find(
    (e) => e.scaleSlug === biggestDelta?.slug
  );

  const score = getUserScaleScore(
    biggestDeltaFound?.scaleScore?.percentScore,
    biggestDelta?.slug as string,
    biggestDeltaFound?.scaleScore?.dimensionSlugSource
  );

  const previousPercentScore = getPreviousPercentScoreFromScoreHistory(
    biggestDeltaFound?.scaleScore?.scoreHistory
  );

  const previousDimensionSlug = getPreviousDimensionSlugSource(
    biggestDeltaFound?.scaleScore?.scoreHistory
  );

  const previousScore = getUserScaleScore(
    previousPercentScore,
    biggestDelta?.slug as string,
    previousDimensionSlug
  );

  const displayedScore = score ? (score * 100).toFixed(0) : 0;

  const displayedPreviousScore = previousScore
    ? (previousScore * 100).toFixed(0)
    : 0;

  const deltaElement = elementJsonData.find(
    (e) => e.slug === biggestDelta?.slug
  );
  const name = deltaElement?.alias ?? deltaElement?.name;

  if (premiumGated) {
    return (
      <main className={styles.main}>
        <p
          style={{ opacity: opacity.step1, flex: 1 }}
          className={styles.subtitle}
        >
          COMPONENT CHANGE
        </p>

        <div
          style={{ flex: 1, opacity: opacity.step2 }}
          className={styles.componentParentDiv}
        >
          <div
            className={styles.scaleRevealIcon}
            style={{ opacity: premiumGated ? 1 : 0.2 }}
          >
            <Pattern patternSlug={props.slug} maxWidth />
          </div>
          <div className={styles.componentLine}></div>
          <div ref={containerRef} className={styles.componentWrapper}>
            {mapped}
          </div>
        </div>

        <div
          style={{ opacity: opacity.step3, flex: 4 }}
          className={styles.scoreHistoryParent}
        >
          <div className={styles.scoreHistoryParentInner}>
            <p className={styles.scoreHistoryParentScore}>
              ??
              <span className={styles.scoreHistorySpan}>/100</span>
            </p>
            <p className={`${font.className} ${styles.scoreHistoryBottomText}`}>
              Previous score
            </p>
          </div>

          <ForwardArrowDeltaScreen />

          <div className={styles.scoreHistoryParentInner}>
            <p className={styles.scoreHistoryParentScore}>
              ??
              <span className={styles.scoreHistorySpan}>/100</span>
            </p>
            <p className={`${font.className} ${styles.scoreHistoryBottomText}`}>
              {"Today's score"}
            </p>
          </div>
        </div>

        <div
          className={styles.linearDiv}
          style={{ opacity: opacity.step4, flex: 3 }}
        >
          <PremiumExclusiveDeniedBAr />
        </div>

        <div className={styles.buttonDiv} style={{ opacity: opacity.step5 }}>
          <ThemedButton
            style={{ width: "100%", fontWeight: "450", padding: "14px" }}
            title="Continue to breakdown"
          />
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        COMPONENT CHANGE
      </p>

      <div
        style={{ flex: 1, opacity: opacity.step2 }}
        className={styles.componentParentDiv}
      >
        <div className={styles.scaleRevealIcon} style={{ opacity: "0.2" }}>
          <Pattern patternSlug={props.slug} maxWidth />
        </div>
        <div className={styles.componentLine}></div>
        <div ref={containerRef} className={styles.componentWrapper}>
          {mapped}
        </div>
      </div>

      <div
        style={{ opacity: opacity.step3, flex: 4 }}
        className={styles.scoreHistoryParent}
      >
        <div className={styles.scoreHistoryParentInner}>
          <p className={styles.scoreHistoryParentScore}>
            {displayedPreviousScore}
            <span className={styles.scoreHistorySpan}>/100</span>
          </p>
          <p className={`${font.className} ${styles.scoreHistoryBottomText}`}>
            Previous score
          </p>
        </div>

        <ForwardArrowDeltaScreen />

        <div className={styles.scoreHistoryParentInner}>
          <p className={styles.scoreHistoryParentScore}>
            {displayedScore}
            <span className={styles.scoreHistorySpan}>/100</span>
          </p>
          <p className={`${font.className} ${styles.scoreHistoryBottomText}`}>
            {"Today's score"}
          </p>
        </div>
      </div>
      <p className={styles.llmText} style={{ opacity: opacity.step4, flex: 4 }}>
        Your {name} score changed the most since last time.
      </p>
    </main>
  );
}

export function TraitAssessmentOutroContext(props: Props) {
  const archetypeDiscoveries = useSelector(
    (state: RootState) => state.user.archetypesDiscovered
  );
  const opacity = useStaggeredOpacity(3);

  const discArchetype = archetypeDiscoveries?.find(
    (a) => a.archetype.archetypeGroupSlug === "work"
  );
  const identityArchetype = archetypeDiscoveries?.find(
    (a) => a.archetype.archetypeGroupSlug === "identity"
  );

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        YOUR CONTEXT
      </p>

      <div
        style={{ opacity: opacity.step2, flex: 4 }}
        className={styles.contextParentDiv}
      >
        <div className={styles.contextParentDivInner}>
          {discArchetype && (
            <div className={styles.scaleRevealIcon}>
              <Archetype archetype={discArchetype.archetype} maxWidth />
            </div>
          )}
          {identityArchetype && (
            <div className={styles.scaleRevealIcon}>
              <Archetype archetype={identityArchetype.archetype} maxWidth />
            </div>
          )}
        </div>
        <p className={styles.bracketText}>{"}"}</p>
        <div className={styles.scaleRevealIcon}>
          {props.type === "pattern" ? (
            <Pattern patternSlug={props.slug} maxWidth />
          ) : (
            <Element slug={props.slug} maxWidth />
          )}
        </div>
      </div>

      <p className={styles.llmText} style={{ opacity: opacity.step3, flex: 8 }}>
        {props.traitAssessmentResult.traitAssessmentLlmData?.userContextBody}
      </p>
    </main>
  );
}

export function TraitAssessmentOutroPositiveAdapatations(props: Props) {
  const opacity = useStaggeredOpacity(3);

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        POSITIVE ADAPTATIONS
      </p>

      <div
        style={{ opacity: opacity.step2, flex: 4 }}
        className={styles.contextParentDiv}
      >
        <div className={styles.scaleRevealIcon}>
          {props.type === "pattern" ? (
            <Pattern patternSlug={props.slug} maxWidth />
          ) : (
            <Element slug={props.slug} maxWidth />
          )}
        </div>
        <p className={styles.bracketText}>{"}"}</p>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="51"
          height="51"
          viewBox="0 0 51 51"
          fill="none"
        >
          <path
            d="M25.502 16.5C20.5332 16.5 16.502 20.5293 16.502 25.5C16.502 30.4668 20.5313 34.5 25.502 34.5C30.4727 34.5 34.502 30.4668 34.502 25.5C34.502 20.5312 30.4727 16.5 25.502 16.5ZM25.502 30C23.0196 30 21.002 27.9785 21.002 25.5C21.002 23.0176 23.0196 21 25.502 21C27.9805 21 30.002 23.0176 30.002 25.5C30.002 27.9785 27.9805 30 25.502 30Z"
            fill="#FFFFF2"
          />
          <path
            d="M25.502 12C26.7442 12 27.752 10.9942 27.752 9.75V5.25C27.752 4.00195 26.7442 3 25.502 3C24.2598 3 23.252 4.00195 23.252 5.25V9.75C23.252 10.9942 24.2598 12 25.502 12Z"
            fill="#FFFFF2"
          />
          <path
            d="M36.6367 16.6113C37.2129 16.6113 37.7891 16.3965 38.2285 15.957L41.4102 12.7715C42.2891 11.8965 42.2891 10.4726 41.4102 9.58984C40.5313 8.71094 39.1074 8.71094 38.2285 9.58984L35.0469 12.7715C34.168 13.6504 34.168 15.0742 35.0469 15.957C35.4863 16.3945 36.0605 16.6113 36.6367 16.6113Z"
            fill="#FFFFF2"
          />
          <path
            d="M45.752 23.25H41.252C40.0078 23.25 39.002 24.2598 39.002 25.5C39.002 26.7442 40.0078 27.75 41.252 27.75H45.752C46.9942 27.75 48.002 26.7442 48.002 25.5C48.002 24.2598 46.9922 23.25 45.752 23.25Z"
            fill="#FFFFF2"
          />
          <path
            d="M38.2227 35.0488C37.3438 34.1699 35.9199 34.1699 35.041 35.0488C34.1621 35.9277 34.1621 37.3516 35.041 38.2305L38.2227 41.4121C38.6621 41.8516 39.2383 42.0703 39.8145 42.0703C40.3907 42.0703 40.9668 41.8516 41.4063 41.4121C42.2852 40.5332 42.2852 39.1094 41.4063 38.2305L38.2227 35.0488Z"
            fill="#FFFFF2"
          />
          <path
            d="M25.4922 39C24.2519 39 23.2422 40.0098 23.2422 41.25V45.75C23.2422 46.9942 24.25 48 25.4922 48C26.7363 48 27.7422 46.9942 27.7422 45.75V41.25C27.7422 40.0098 26.7363 39 25.4922 39Z"
            fill="#FFFFF2"
          />
          <path
            d="M12.7676 35.0391L9.58593 38.2207C8.70703 39.0996 8.70703 40.5235 9.58593 41.4063C10.0254 41.8457 10.6015 42.0606 11.1777 42.0606C11.7539 42.0606 12.3301 41.8457 12.7695 41.4063L15.9512 38.2207C16.8301 37.3457 16.8301 35.9219 15.9512 35.0391C15.0703 34.1602 13.6465 34.1602 12.7676 35.0391Z"
            fill="#FFFFF2"
          />
          <path
            d="M11.998 25.4961C11.998 24.2519 10.9922 23.2461 9.74805 23.2461H5.24805C4.0039 23.2461 2.99805 24.2519 2.99805 25.4961C2.99805 26.7363 4.0039 27.7461 5.24805 27.7461H9.74805C10.9922 27.7461 11.998 26.7344 11.998 25.4961Z"
            fill="#FFFFF2"
          />
          <path
            d="M12.7773 15.9473C13.2168 16.3867 13.791 16.6113 14.3691 16.6113C14.9433 16.6113 15.5215 16.3867 15.959 15.9473C16.8379 15.0723 16.8379 13.6484 15.959 12.7695L12.7773 9.58789C11.8984 8.70899 10.4746 8.70899 9.5957 9.58789C8.7168 10.4629 8.7168 11.8867 9.5957 12.7695L12.7773 15.9473Z"
            fill="#FFFFF2"
          />
        </svg>
      </div>

      <p className={styles.llmText} style={{ opacity: opacity.step3, flex: 8 }}>
        {
          props.traitAssessmentResult.traitAssessmentLlmData
            ?.positiveAdaptationsBody
        }
      </p>
    </main>
  );
}

export function TraitAssessmentOutroNegativeAdapatations(props: Props) {
  const opacity = useStaggeredOpacity(3);

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        NEGATIVE ADAPTATIONS
      </p>

      <div
        style={{ opacity: opacity.step2, flex: 4 }}
        className={styles.contextParentDiv}
      >
        <div className={styles.scaleRevealIcon}>
          {props.type === "pattern" ? (
            <Pattern patternSlug={props.slug} maxWidth />
          ) : (
            <Element slug={props.slug} maxWidth />
          )}
        </div>
        <p className={styles.bracketText}>{"}"}</p>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="51"
          height="51"
          viewBox="0 0 51 51"
          fill="none"
        >
          <g clipPath="url(#clip0_69959_18488)">
            <path
              d="M13.2969 36.0684C12.3418 36.0684 11.5664 36.8418 11.5664 37.7969V40.8477C11.5664 41.8028 12.3418 42.5762 13.2969 42.5762C14.2519 42.5762 15.0273 41.8027 15.0273 40.8477V37.7969C15.0273 36.8418 14.2519 36.0684 13.2969 36.0684Z"
              fill="#FFFFF2"
            />
            <path
              d="M7.19529 34.2363C6.24019 34.2363 5.46484 35.0098 5.46484 35.9648V39.0156C5.46484 39.9707 6.24024 40.7441 7.19529 40.7441C8.15034 40.7441 8.92574 39.9707 8.92574 39.0156V35.9648C8.92574 35.0117 8.15034 34.2363 7.19529 34.2363Z"
              fill="#FFFFF2"
            />
            <path
              d="M43.8047 33.627C42.8496 33.627 42.0742 34.4004 42.0742 35.3555V38.4063C42.0742 39.3614 42.8496 40.1348 43.8047 40.1348C44.7597 40.1348 45.5351 39.3613 45.5351 38.4063V35.3555C45.5351 34.4004 44.7597 33.627 43.8047 33.627Z"
              fill="#FFFFF2"
            />
            <path
              d="M39.617 10.594C37.0526 10.594 34.6307 11.4846 32.7145 13.0647C30.611 7.94769 25.6285 4.49219 19.855 4.49219C13.0095 4.49219 7.31601 9.33984 6.14801 15.9532C2.81011 17.1192 0.499512 20.2891 0.499512 23.9162C0.499512 28.5705 4.28661 32.3577 8.94101 32.3577H14.7205V28.8987H8.94101C6.19491 28.8987 3.95861 26.6643 3.95861 23.9163C3.95861 21.5198 5.66761 19.4612 8.02501 19.0237C8.78671 18.8811 9.36291 18.2502 9.43126 17.4768C9.91759 12.0473 14.4 7.95129 19.8553 7.95129C25.0563 7.95129 29.4043 11.6837 30.1933 16.8283C30.3026 17.5412 30.8417 18.1095 31.5468 18.258C32.2538 18.4064 32.9765 18.1017 33.3632 17.4963C34.7382 15.34 37.0761 14.0529 39.6192 14.0529C43.7129 14.0529 47.0412 17.383 47.0412 21.4749C47.0412 25.5687 43.7111 28.8989 39.6172 28.8989H36.4805V33.6782C36.4805 34.6333 37.2559 35.4067 38.2109 35.4067C39.166 35.4067 39.9414 34.6333 39.9414 33.6782V32.3403C45.7909 32.1665 50.4999 27.3657 50.4999 21.4753C50.4999 15.4753 45.6192 10.594 39.617 10.594Z"
              fill="#FFFFF2"
            />
            <path
              d="M31.539 29.1209L23.3925 31.0213L29.7695 22.7343C30.3516 21.9765 30.2109 20.8906 29.4531 20.3085C28.6973 19.7265 27.6094 19.8671 27.0273 20.6249L17.6973 32.7519C17.252 33.332 17.2188 34.1289 17.6133 34.7422C18.0098 35.3574 18.75 35.6562 19.461 35.4902L27.582 33.5957L23.5351 38.8202L21.1073 36.9393L19.8026 46.5078L28.7421 42.8535L26.2714 40.9395L33.3024 31.8655C33.7497 31.2874 33.7868 30.4885 33.3903 29.8733C32.9919 29.2561 32.25 28.9549 31.539 29.1209Z"
              fill="#FFFFF2"
            />
          </g>
          <defs>
            <clipPath id="clip0_69959_18488">
              <rect
                width="50"
                height="50"
                fill="white"
                transform="translate(0.5 0.5)"
              />
            </clipPath>
          </defs>
        </svg>
      </div>

      <p className={styles.llmText} style={{ opacity: opacity.step3, flex: 8 }}>
        {
          props.traitAssessmentResult.traitAssessmentLlmData
            ?.negativeAdaptationsBody
        }
      </p>
    </main>
  );
}

export function TraitAssessmentOutroOrigins(props: Props) {
  const opacity = useStaggeredOpacity(3);

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        ORIGINS
      </p>

      <div
        style={{ opacity: opacity.step2, flex: 4 }}
        className={styles.contextParentDiv}
      >
        <div className={styles.scaleRevealIcon}>
          {props.type === "pattern" ? (
            <Pattern patternSlug={props.slug} maxWidth />
          ) : (
            <Element slug={props.slug} maxWidth />
          )}
        </div>
        <p className={styles.bracketText}>{"}"}</p>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="51"
          height="51"
          viewBox="0 0 51 51"
          fill="none"
        >
          <path
            d="M25.5 45C24.5613 45 23.7927 44.2197 23.7927 43.2667V32H22.5122C14.5309 32 8 25.3696 8 17.2667V14.2333C8 13.2803 8.76863 12.5 9.70732 12.5H12.6951C17.2618 12.5 21.3602 14.6667 24.0496 18.0471C25.2868 11.2 31.2191 6 38.3049 6H41.2927C42.2314 6 43 6.78035 43 7.73333V10.7667C43 18.8696 36.4691 25.5 28.4878 25.5H27.2073V43.2667C27.2073 44.2197 26.4387 45 25.5 45ZM11.4146 15.9667V17.2667C11.4146 23.4638 16.4081 28.5333 22.5122 28.5333H23.7927V27.2333C23.7927 21.0362 18.7992 15.9667 12.6951 15.9667H11.4146ZM27.2073 22.0333H28.4878C34.5919 22.0333 39.5854 16.9638 39.5854 10.7667V9.46667H38.3049C32.2008 9.46667 27.2073 14.5362 27.2073 20.7333V22.0333Z"
            fill="#FFFFF2"
          />
        </svg>
      </div>

      <p className={styles.llmText} style={{ opacity: opacity.step3, flex: 8 }}>
        {props.traitAssessmentResult.traitAssessmentLlmData?.originsBody}
      </p>
    </main>
  );
}

export function TraitAssessmentOutroTakeaways(props: Props) {
  const imageUrl = useSelector((state: RootState) => state.user.imageURL);
  const publicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const opacity = useStaggeredOpacity(3);

  return (
    <main className={styles.main}>
      <p
        style={{ opacity: opacity.step1, flex: 1 }}
        className={styles.subtitle}
      >
        TAKE AWAY
      </p>

      <div
        style={{ opacity: opacity.step2, flex: 4 }}
        className={styles.contextParentDiv}
      >
        <div className={styles.scaleRevealIcon}>
          {props.type === "pattern" ? (
            <Pattern patternSlug={props.slug} maxWidth />
          ) : (
            <Element slug={props.slug} maxWidth />
          )}
        </div>
        <p className={styles.bracketText}>{"}"}</p>
        <SmallProfileImage
          medium
          publicProfile={publicProfile}
          imageUrl={imageUrl ?? ""}
        />
      </div>

      <p className={styles.llmText} style={{ opacity: opacity.step3, flex: 8 }}>
        {props.traitAssessmentResult.traitAssessmentLlmData?.takeawayBody}
      </p>
    </main>
  );
}
